/**
 * How the transcript fold reads OpenCode's `subagent` tool: the child session a call runs in,
 * and what it ended with. OpenCode wraps a call's report in a `<subagent …>` envelope and its
 * errors in sentences that name the child; they are read here, once, so a step carries the same
 * clean text whether a saved message or live events built it. When it ran is the step's own
 * clock, as for any tool. While a call runs, its turn relays what the child does, named by
 * child, which is matched to the call here too.
 */
import type { SubagentActivity, SubagentWork } from '@/lib/code-sessions/subagent-activity';
import type { CodeSessionAgentEvent } from '@/lib/code-sessions/types';
import type { SessionMessageAssistant } from '@opencode/client/promise';

import type { TraceStep } from './transcript-model';

import { childTitled, subagentActivityEventOf } from '@/lib/code-sessions/subagent-activity';
import { stringInput } from '@/lib/code-sessions/tool-steps';
import { getRecord } from '@/lib/utils/unknown';

type ToolContent = Extract<SessionMessageAssistant['content'][number], { type: 'tool' }>;

/** A `subagent` call as the lead knows it; saved content and live events build it alike. */
export type SubagentCall = {
  /** The tool call's ID, used for React keys and group keys. */
  callId: string;
  /**
   * The child, once named: progress or result metadata, or a continuing call's input; else the
   * child whose relayed title is the call's description (`showSubagentActivity`).
   */
  childSessionId?: string;
  /** Once completed: the report's first line, plain text (OpenCode's no-text sentence as is). */
  report?: string;
  /** While it runs: what its child does now, from the newest activity its turn relayed. */
  activity?: SubagentWork;
};

/**
 * The child session a subagent call runs in: OpenCode names it in the call's
 * metadata with its progress and again with its result, and a call that
 * continues a child names it in its input too.
 */
function childSessionIdOf(metadata: unknown, input: unknown): string | undefined {
  return stringInput(metadata, ['sessionID']) || stringInput(input, ['sessionID']) || undefined;
}

// `<subagent sessionID="ses_…" state="completed">`, and its closing tag, which a report
// OpenCode cut short at its size limit lacks.
const REPORT_ENVELOPE_START = /^\s*<subagent\b[^>]*>/;
const REPORT_ENVELOPE_END = /<\/subagent>\s*$/;

/** One line of Markdown as plain text: no heading, list or quote marker, emphasis or link syntax. */
function plainLine(line: string): string {
  return line
    .replace(/^\s*(?:#{1,6}\s+|>\s*|[-*+]\s+|\d+[.)]\s+)/, '')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\*\*|__|`/g, '')
    .trim();
}

/**
 * The first line of a completed call's report, as plain text. The report is the tool result's
 * first text item; a truncated one is followed by a second item pointing at the full output.
 */
function subagentReport(content: unknown): string {
  const first = Array.isArray(content)
    ? content.map((item) => getRecord(item)?.text).find((text) => typeof text === 'string')
    : undefined;
  const report = (typeof first === 'string' ? first : '')
    .replace(REPORT_ENVELOPE_START, '')
    .replace(REPORT_ENVELOPE_END, '');
  for (const line of report.split('\n')) {
    // A code fence opens or closes a block, which says nothing on its own.
    if (line.trimStart().startsWith('```')) {
      continue;
    }
    const plain = plainLine(line);
    if (plain.length > 0) {
      return plain;
    }
  }
  return '';
}

/** A saved `subagent` call as the lead's message keeps it. */
export function savedSubagentCall(content: ToolContent): SubagentCall {
  const { state } = content;
  const input = 'input' in state ? state.input : undefined;
  const metadata = 'metadata' in state ? state.metadata : undefined;
  return {
    callId: content.id,
    childSessionId: childSessionIdOf(metadata, input),
    report: state.status === 'completed' ? subagentReport(state.content) : undefined,
  };
}

/**
 * Folds one of the call's live events into it: a success keeps its report, and every event that
 * names the child keeps it.
 */
export function foldSubagentEvent(
  call: SubagentCall,
  event: Pick<CodeSessionAgentEvent, 'type' | 'data'>,
): void {
  const data = getRecord(event.data) ?? {};
  call.childSessionId = childSessionIdOf(data.metadata, data.input) ?? call.childSessionId;
  if (event.type === 'session.tool.success') {
    call.report = subagentReport(data.content);
  }
}

/**
 * What the turn last relayed of each subagent, by child session, in the order the page first
 * heard of each: the child's newest activity and its title, once one event carried it.
 */
export type SubagentActivities = Map<string, { title?: string; activity: SubagentActivity }>;

/**
 * Notes one relayed `subagent.activity` event: the child's newest activity wins, and its title
 * stays. Kept whether or not its call's own events came yet, since the session's log can reach
 * the page minutes after the activity it led to.
 */
export function noteSubagentActivity(activities: SubagentActivities, data: unknown): void {
  const relayed = subagentActivityEventOf(data);
  if (relayed !== undefined) {
    activities.set(relayed.sessionID, {
      title: relayed.title ?? activities.get(relayed.sessionID)?.title,
      activity: relayed.activity,
    });
  }
}

/**
 * Gives each running subagent call what its child does now. A call knows its child once OpenCode
 * names it, by the call's progress or result or a continuing call's input; one that has not
 * learned it takes the child titled with its description (`childTitled`) among those no other
 * call has, in the order the page first heard of them, and keeps it, so its link opens that child
 * without a lookup. An idle child shows nothing, and neither does a settled call.
 */
export function showSubagentActivity(
  steps: readonly { running?: boolean; detail: string; subagent?: SubagentCall }[],
  activities: SubagentActivities,
): void {
  const taken = new Set(steps.flatMap((step) => step.subagent?.childSessionId ?? []));
  const children = [...activities].map(([id, { title }]) => ({ id, title }));
  for (const { running, detail, subagent: call } of steps) {
    if (call === undefined || running !== true) {
      continue;
    }
    const titled =
      call.childSessionId === undefined ? childTitled(detail, children, taken) : undefined;
    if (titled !== undefined) {
      taken.add(titled);
      call.childSessionId = titled;
    }
    const latest =
      call.childSessionId === undefined ? undefined : activities.get(call.childSessionId)?.activity;
    call.activity = latest?.kind === 'idle' ? undefined : latest;
  }
}

const SUBAGENT_CANCELLED = /^Subagent cancelled \(sessionID: [^)]*\)/;
const SUBAGENT_FAILED = /^Subagent failed \(sessionID: [^)]*\): ([\s\S]*\S[\s\S]*)$/;

/**
 * What a failed call's error says of its child. "Subagent cancelled (sessionID: …)", which
 * anything that interrupts the child (the tab's Stop) gives, is a stop, not a failure.
 * "Subagent failed (sessionID: …): <error>" is the child's own error, which is kept whole,
 * lines included. Anything else, such as a refusal before the child started, stays as it is.
 * A call cut short by the lead's own interrupt fails `aborted`, which every tool's step reads
 * as interrupted before it gets here.
 */
export function subagentFailure(
  message: string | undefined,
): Pick<TraceStep, 'failed' | 'interrupted' | 'error'> {
  if (message === undefined) {
    return { failed: true };
  }
  if (SUBAGENT_CANCELLED.test(message)) {
    return { interrupted: true };
  }
  return { failed: true, error: SUBAGENT_FAILED.exec(message)?.[1] ?? message };
}
